import { getBankAccounts } from '@/data/supabase/bank-accounts';
import { supabase } from '@/integrations/supabase/client';

/**
 * Result of converting vendor invoices to expense transactions
 */
export interface ConvertInvoicesResult {
  total: number;
  converted: number;
  skipped: number;
  errors: number;
}

/**
 * Converts completed vendor invoices to expense transactions in bank_transactions table.
 * This ensures COGS data flows into the financial calculations (P&L, KPIs, etc.)
 * 
 * @param companyId - The company ID to process invoices for
 * @param vendorId - Optional vendor ID to filter by specific vendor
 * @returns Result object with counts of processed invoices
 */
export async function convertVendorInvoicesToExpenses(
  companyId: string,
  vendorId?: string
): Promise<ConvertInvoicesResult> {
  const result: ConvertInvoicesResult = {
    total: 0,
    converted: 0,
    skipped: 0,
    errors: 0,
  };

  try {
    // Build query for completed invoices with total amounts
    let query = supabase
      .from('vendor_invoice_training')
      .select(`
        id,
        vendor_id,
        invoice_number,
        invoice_date,
        total_amount,
        ocr_status,
        vendors:vendor_id (
          id,
          name
        )
      `)
      .eq('company_id', companyId)
      .eq('ocr_status', 'completed')
      .not('total_amount', 'is', null)
      .gt('total_amount', 0);

    if (vendorId) {
      query = query.eq('vendor_id', vendorId);
    }

    const { data: invoices, error: invoicesError } = await query;

    if (invoicesError) {
      console.error('Error fetching invoices:', invoicesError);
      throw invoicesError;
    }

    if (!invoices || invoices.length === 0) {
      return result;
    }

    result.total = invoices.length;

    // Check which invoices already have corresponding expense transactions
    const invoiceIds = invoices.map(inv => inv.id);
    const { data: existingTransactions, error: existingError } = await supabase
      .from('bank_transactions')
      .select('source_reference_id')
      .eq('company_id', companyId)
      .eq('source', 'vendor_invoice')
      .in('source_reference_id', invoiceIds);

    if (existingError) {
      console.error('Error checking existing transactions:', existingError);
      // Continue anyway, we'll handle duplicates individually
    }

    const existingInvoiceIds = new Set(
      (existingTransactions || []).map(t => t.source_reference_id)
    );

    // bank_transactions requires account_id: use first active bank account for the company
    const accounts = await getBankAccounts(companyId);
    const accountId = accounts[0]?.id;
    if (!accountId) {
      console.warn(
        '[convertVendorInvoicesToExpenses] No bank account for company; skipping expense inserts. Add a bank account to sync vendor invoice expenses.'
      );
      return result;
    }

    // Convert invoices to expense transactions
    const transactionsToInsert: any[] = [];

    for (const invoice of invoices) {
      // Skip if already converted
      if (existingInvoiceIds.has(invoice.id)) {
        result.skipped++;
        continue;
      }

      const vendorName = (invoice.vendors as any)?.name || 'Unknown Vendor';
      const invoiceNumber = invoice.invoice_number || 'N/A';
      const invoiceDate = invoice.invoice_date || new Date().toISOString().split('T')[0];
      const totalAmount = Number(invoice.total_amount);

      // Create expense transaction (negative amount for expense)
      transactionsToInsert.push({
        account_id: accountId,
        company_id: companyId,
        transaction_date: invoiceDate,
        amount: -Math.abs(totalAmount), // Ensure negative for expense
        description: `${vendorName} - Invoice #${invoiceNumber}`,
        merchant_name: vendorName,
        category_type: 'expenses',
        subcategory: 'cogs',
        detail_source: `Vendor Invoice: ${invoiceNumber}`,
        source: 'vendor_invoice',
        source_reference_id: invoice.id,
        period_flag: 'training_window', // Mark as training data initially
        auto_categorized: true,
        needs_review: false,
        classification_confidence: 1.0,
        classification_method: 'manual',
      });
    }

    // Insert transactions in batches
    if (transactionsToInsert.length > 0) {
      const batchSize = 50;
      for (let i = 0; i < transactionsToInsert.length; i += batchSize) {
        const batch = transactionsToInsert.slice(i, i + batchSize);
        
        const { error: insertError } = await supabase
          .from('bank_transactions')
          .insert(batch);

        if (insertError) {
          console.error('Error inserting expense transactions:', insertError);
          result.errors += batch.length;
        } else {
          result.converted += batch.length;
        }
      }
    }

    return result;
  } catch (error) {
    console.error('Error converting vendor invoices to expenses:', error);
    throw error;
  }
}

/**
 * Checks if a vendor invoice has already been converted to an expense transaction
 * 
 * @param companyId - The company ID
 * @param invoiceId - The vendor invoice ID to check
 * @returns True if already converted, false otherwise
 */
export async function isInvoiceAlreadyConverted(
  companyId: string,
  invoiceId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from('bank_transactions')
    .select('id')
    .eq('company_id', companyId)
    .eq('source', 'vendor_invoice')
    .eq('source_reference_id', invoiceId)
    .maybeSingle();

  if (error) {
    console.error('Error checking if invoice is converted:', error);
    return false;
  }

  return !!data;
}

/**
 * Gets the total COGS expenses from vendor invoices for a date range
 * 
 * @param companyId - The company ID
 * @param startDate - Start date (YYYY-MM-DD)
 * @param endDate - End date (YYYY-MM-DD)
 * @returns Total COGS amount (positive number)
 */
export async function getVendorInvoiceCOGS(
  companyId: string,
  startDate: string,
  endDate: string
): Promise<number> {
  const { data, error } = await supabase
    .from('bank_transactions')
    .select('amount')
    .eq('company_id', companyId)
    .eq('source', 'vendor_invoice')
    .eq('category_type', 'expenses')
    .eq('subcategory', 'cogs')
    .gte('transaction_date', startDate)
    .lte('transaction_date', endDate);

  if (error) {
    console.error('Error fetching vendor invoice COGS:', error);
    return 0;
  }

  // Sum absolute values (amounts are stored as negative)
  return (data || []).reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0);
}
